/**
 * CurrentStatePanel - Live Digital Twin State Display
 *
 * Shows real-time WITS market data alongside modelled solver output.
 * This is the core digital twin paradigm: LIVE (what IS) vs MODELLED (what we predict).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { nzGridApi } from '../services/api';
import DataBadge from './DataBadge';

interface WITSSchedule {
  schedule: string;
  runType?: string;
  marketType: string;
}

interface WITSPriceRecord {
  node: string;
  price: number;
  tradingPeriod: number;
  tradingDateTime: string;
  schedule?: string;
}

interface WITSEnergyRecord {
  tradingPeriod: number;
  tradingDateTime: string;
  load?: number;
  generation?: number;
  intermittentGeneration?: number;
  island?: string;
}

interface WITSReserveRecord {
  tradingPeriod: number;
  tradingDateTime: string;
  reserveMw?: number;
  riskMw?: number;
  price?: number;
  island?: string;
  reserveType?: string;
}

interface CurrentStatePanelProps {
  // Modelled data from solver for comparison
  modelledPrices?: { node: string; lmp: number }[];
  modelledGeneration?: number; // Total MW
  modelledLoad?: number; // Total MW
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

const CurrentStatePanel: React.FC<CurrentStatePanelProps> = ({
  modelledPrices = [],
  modelledGeneration,
  modelledLoad,
  isCollapsed = false,
  onToggleCollapse,
}) => {
  // Live WITS data state
  const [schedules, setSchedules] = useState<WITSSchedule[]>([]);
  const [livePrices, setLivePrices] = useState<WITSPriceRecord[]>([]);
  const [liveEnergy, setLiveEnergy] = useState<WITSEnergyRecord[]>([]);
  const [liveReserves, setLiveReserves] = useState<WITSReserveRecord[]>([]);

  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Fetch all live data
  const fetchLiveData = useCallback(async () => {
    setLoading({ schedules: true, prices: true, energy: true, reserves: true });

    try {
      // Fetch in parallel
      const [schedulesData, pricesData, energyData, reservesData] = await Promise.allSettled([
        nzGridApi.getWITSSchedules(),
        nzGridApi.getWITSPricesRolling('RTD', 3, 3, undefined, 'E'),
        nzGridApi.getWITSEnergy(),
        nzGridApi.getWITSReserves('PRS', 'InstantaneousReserve'),
      ]);

      // Process schedules
      if (schedulesData.status === 'fulfilled' && schedulesData.value) {
        setSchedules(schedulesData.value);
        setErrors(prev => ({ ...prev, schedules: null }));
      } else {
        setErrors(prev => ({ ...prev, schedules: 'Failed to load schedules' }));
      }

      // Process prices
      if (pricesData.status === 'fulfilled' && pricesData.value) {
        // Handle both array and wrapped response
        const prices = Array.isArray(pricesData.value)
          ? pricesData.value
          : pricesData.value.prices || [];
        setLivePrices(prices);
        setErrors(prev => ({ ...prev, prices: null }));
      } else {
        setErrors(prev => ({ ...prev, prices: 'Failed to load prices' }));
      }

      // Process energy
      if (energyData.status === 'fulfilled' && energyData.value) {
        const energy = Array.isArray(energyData.value)
          ? energyData.value
          : energyData.value.energy || [];
        setLiveEnergy(energy);
        setErrors(prev => ({ ...prev, energy: null }));
      } else {
        setErrors(prev => ({ ...prev, energy: 'Failed to load energy' }));
      }

      // Process reserves
      if (reservesData.status === 'fulfilled' && reservesData.value) {
        const reserves = Array.isArray(reservesData.value)
          ? reservesData.value
          : reservesData.value.reserves || [];
        setLiveReserves(reserves);
        setErrors(prev => ({ ...prev, reserves: null }));
      } else {
        setErrors(prev => ({ ...prev, reserves: 'Failed to load reserves' }));
      }

      setLastUpdated(new Date());
    } finally {
      setLoading({ schedules: false, prices: false, energy: false, reserves: false });
    }
  }, []);

  // Initial fetch and periodic refresh
  useEffect(() => {
    fetchLiveData();
    const interval = setInterval(fetchLiveData, 60_000); // Refresh every minute
    return () => clearInterval(interval);
  }, [fetchLiveData]);

  // Calculate summary statistics
  const priceStats = React.useMemo(() => {
    if (livePrices.length === 0) return null;
    const prices = livePrices.map(p => p.price).filter(p => typeof p === 'number');
    if (prices.length === 0) return null;
    return {
      min: Math.min(...prices),
      max: Math.max(...prices),
      avg: prices.reduce((a, b) => a + b, 0) / prices.length,
      count: prices.length,
    };
  }, [livePrices]);

  const energyStats = React.useMemo(() => {
    if (liveEnergy.length === 0) return null;
    const latest = liveEnergy[liveEnergy.length - 1];
    return {
      load: latest?.load ?? null,
      generation: latest?.generation ?? null,
      intermittent: latest?.intermittentGeneration ?? null,
      period: latest?.tradingPeriod ?? null,
    };
  }, [liveEnergy]);

  const reserveStats = React.useMemo(() => {
    if (liveReserves.length === 0) return null;
    // Get latest by island
    const niReserves = liveReserves.filter(r => r.island === 'NI');
    const siReserves = liveReserves.filter(r => r.island === 'SI');
    const latestNI = niReserves[niReserves.length - 1];
    const latestSI = siReserves[siReserves.length - 1];
    return {
      ni: latestNI?.reserveMw ?? null,
      si: latestSI?.reserveMw ?? null,
      niRisk: latestNI?.riskMw ?? null,
      siRisk: latestSI?.riskMw ?? null,
    };
  }, [liveReserves]);

  // Compare with modelled data
  const comparison = React.useMemo(() => {
    if (!modelledPrices || modelledPrices.length === 0 || livePrices.length === 0) return null;

    // Find matching nodes and compute deltas
    const deltas: { node: string; live: number; modelled: number; delta: number }[] = [];

    for (const modelled of modelledPrices) {
      const liveMatch = livePrices.find(p =>
        p.node?.toLowerCase().includes(modelled.node.toLowerCase().slice(0, 3))
      );
      if (liveMatch && typeof liveMatch.price === 'number') {
        deltas.push({
          node: modelled.node,
          live: liveMatch.price,
          modelled: modelled.lmp,
          delta: liveMatch.price - modelled.lmp,
        });
      }
    }

    return deltas.length > 0 ? deltas : null;
  }, [livePrices, modelledPrices]);

  const hasAnyError = Object.values(errors).some(e => e !== null);
  const isAnyLoading = Object.values(loading).some(l => l);

  if (isCollapsed) {
    return (
      <div
        className="bg-gray-800/90 backdrop-blur-sm rounded-lg p-2 cursor-pointer hover:bg-gray-700/90 transition-colors"
        onClick={onToggleCollapse}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-300">Current State</span>
          <DataBadge tier="LIVE" label="LIVE" showDot />
          {isAnyLoading && <span className="text-xs text-gray-500 animate-pulse">...</span>}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800/95 backdrop-blur-sm rounded-lg shadow-xl border border-gray-700/50 overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-2 bg-gray-900/50 border-b border-gray-700/50 cursor-pointer hover:bg-gray-900/70"
        onClick={onToggleCollapse}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-white">Current State</span>
          <DataBadge tier="LIVE" label="WITS" showDot />
          {isAnyLoading && <span className="text-xs text-blue-400 animate-pulse">updating...</span>}
        </div>
        <div className="flex items-center gap-2">
          {lastUpdated && (
            <span className="text-xs text-gray-500">
              {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); fetchLiveData(); }}
            className="text-xs text-blue-400 hover:text-blue-300 px-2 py-1 rounded hover:bg-gray-700"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
        {/* Energy Market Prices */}
        <section>
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
            Energy Prices (RTD)
          </h3>
          {errors.prices ? (
            <div className="text-xs text-red-400">{errors.prices}</div>
          ) : priceStats ? (
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div className="bg-gray-700/50 rounded p-2">
                <div className="text-gray-400 text-xs">Min</div>
                <div className="text-green-400 font-mono">${priceStats.min.toFixed(2)}</div>
              </div>
              <div className="bg-gray-700/50 rounded p-2">
                <div className="text-gray-400 text-xs">Avg</div>
                <div className="text-yellow-400 font-mono">${priceStats.avg.toFixed(2)}</div>
              </div>
              <div className="bg-gray-700/50 rounded p-2">
                <div className="text-gray-400 text-xs">Max</div>
                <div className="text-red-400 font-mono">${priceStats.max.toFixed(2)}</div>
              </div>
            </div>
          ) : (
            <div className="text-xs text-gray-500">No price data</div>
          )}
        </section>

        {/* Energy Quantities */}
        <section>
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
            Energy Balance
          </h3>
          {errors.energy ? (
            <div className="text-xs text-red-400">{errors.energy}</div>
          ) : energyStats ? (
            <div className="space-y-2">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-400">Generation</span>
                <div className="flex items-center gap-2">
                  <span className="text-green-400 font-mono">
                    {energyStats.generation?.toFixed(0) ?? '—'} MW
                  </span>
                  {modelledGeneration && (
                    <span className="text-xs text-gray-500">
                      (modelled: {modelledGeneration.toFixed(0)})
                    </span>
                  )}
                </div>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-400">Load</span>
                <div className="flex items-center gap-2">
                  <span className="text-blue-400 font-mono">
                    {energyStats.load?.toFixed(0) ?? '—'} MW
                  </span>
                  {modelledLoad && (
                    <span className="text-xs text-gray-500">
                      (modelled: {modelledLoad.toFixed(0)})
                    </span>
                  )}
                </div>
              </div>
              {energyStats.intermittent !== null && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-400">Intermittent</span>
                  <span className="text-amber-400 font-mono">
                    {energyStats.intermittent?.toFixed(0) ?? '—'} MW
                  </span>
                </div>
              )}
              {energyStats.period && (
                <div className="text-xs text-gray-500 text-right">
                  Trading Period {energyStats.period}
                </div>
              )}
            </div>
          ) : (
            <div className="text-xs text-gray-500">No energy data</div>
          )}
        </section>

        {/* Reserves */}
        <section>
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
            Instantaneous Reserve
          </h3>
          {errors.reserves ? (
            <div className="text-xs text-red-400">{errors.reserves}</div>
          ) : reserveStats ? (
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="bg-gray-700/50 rounded p-2">
                <div className="text-gray-400 text-xs">North Island</div>
                <div className="text-purple-400 font-mono">
                  {reserveStats.ni?.toFixed(0) ?? '—'} MW
                </div>
                {reserveStats.niRisk && (
                  <div className="text-xs text-gray-500">
                    Risk: {reserveStats.niRisk.toFixed(0)} MW
                  </div>
                )}
              </div>
              <div className="bg-gray-700/50 rounded p-2">
                <div className="text-gray-400 text-xs">South Island</div>
                <div className="text-purple-400 font-mono">
                  {reserveStats.si?.toFixed(0) ?? '—'} MW
                </div>
                {reserveStats.siRisk && (
                  <div className="text-xs text-gray-500">
                    Risk: {reserveStats.siRisk.toFixed(0)} MW
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-xs text-gray-500">No reserve data</div>
          )}
        </section>

        {/* Live vs Modelled Comparison */}
        {comparison && comparison.length > 0 && (
          <section>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Live vs Modelled (LMP Delta)
            </h3>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {comparison.slice(0, 10).map((c, i) => (
                <div key={i} className="flex justify-between items-center text-xs">
                  <span className="text-gray-400 font-mono">{c.node}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-green-400">${c.live.toFixed(2)}</span>
                    <span className="text-gray-500">vs</span>
                    <span className="text-blue-400">${c.modelled.toFixed(2)}</span>
                    <span className={`font-mono ${c.delta > 0 ? 'text-red-400' : 'text-green-400'}`}>
                      {c.delta > 0 ? '+' : ''}{c.delta.toFixed(2)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Available Schedules */}
        {schedules.length > 0 && (
          <section>
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
              Available Schedules
            </h3>
            <div className="flex flex-wrap gap-1">
              {schedules.map((s, i) => (
                <span
                  key={i}
                  className="px-2 py-0.5 bg-gray-700 rounded text-xs text-gray-300"
                  title={`${s.runType || ''} - ${s.marketType === 'E' ? 'Energy' : 'Reserve'}`}
                >
                  {s.schedule}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Data Provenance Footer */}
        <div className="pt-2 border-t border-gray-700/50">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500">Data Source</span>
            <div className="flex items-center gap-1">
              <DataBadge tier="LIVE" label="WITS API" showDot />
              {hasAnyError && <span className="text-red-400">Some data unavailable</span>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CurrentStatePanel;
